import type { Error as WireError } from '@gen/chat/v1/common_pb';

/**
 * The Section 04 error contract, expressed as the decision the client has to
 * make rather than as a list of strings.
 *
 * The mapping is deliberately explicit and closed. An unrecognized code is
 * treated as retryable-with-backoff rather than terminal, because a server that
 * has learned a new code should not cause this client to discard a message the
 * user believes is queued; the operation stays visible and the user can cancel
 * it.
 */
export type ErrorDisposition =
  /** Back off and retry the same intent under the same operation identifier. */
  | 'RETRY'
  /** Stop. The user must change something; no automatic retry may be queued. */
  | 'TERMINAL'
  /** Credentials are the problem. Refresh or sign in; never spin a reconnect loop. */
  | 'REAUTH'
  /** Catch up from the last locally committed cursor, then continue. */
  | 'RESYNC'
  /** Take a stable snapshot and replay its tail; preserve outgoing work. */
  | 'RESET';

const DISPOSITIONS: Readonly<Record<string, ErrorDisposition>> = {
  UNAUTHENTICATED: 'REAUTH',
  SESSION_REVOKED: 'REAUTH',

  FORBIDDEN: 'TERMINAL',
  NOT_FOUND: 'TERMINAL',
  INVALID_ARGUMENT: 'TERMINAL',
  PAYLOAD_TOO_LARGE: 'TERMINAL',
  // Reusing this identifier with modified input is exactly what the server is
  // objecting to, so a retry can only repeat the objection.
  OPERATION_CONFLICT: 'TERMINAL',
  // The user has to see the current state before another mutation is sensible.
  VERSION_CONFLICT: 'TERMINAL',
  UNSUPPORTED_VERSION: 'TERMINAL',
  // A server that does not implement a method will not implement it by being
  // asked again. Retrying was far worse than a failed action: the outbox allows
  // one mutation in flight per conversation and preserves order, so a single
  // unsupported operation requeued forever blocked every later mutation in that
  // conversation — including sending messages. One unavailable feature took the
  // conversation with it.
  UNSUPPORTED_METHOD: 'TERMINAL',
  UPLOAD_EXPIRED: 'TERMINAL',

  RATE_LIMITED: 'RETRY',
  OVERLOADED: 'RETRY',
  TEMPORARILY_UNAVAILABLE: 'RETRY',
  INTERNAL: 'RETRY',
  MEDIA_NOT_READY: 'RETRY',

  RESYNC_REQUIRED: 'RESYNC',
  RESET_REQUIRED: 'RESET',
};

export function dispositionFor(code: string): ErrorDisposition {
  return DISPOSITIONS[code] ?? 'RETRY';
}

/**
 * A failure that came back from the server with a code, as opposed to a
 * transport failure that produced no answer at all.
 */
export class RemoteError extends Error {
  readonly code: string;
  readonly retryable: boolean;
  readonly retryAfterMs: number | null;
  readonly currentVersion: number | null;

  constructor(wire: WireError) {
    super(wire.message || wire.code);
    this.name = 'RemoteError';
    this.code = wire.code;
    this.retryable = wire.retryable;
    this.retryAfterMs = wire.retryAfterMs ?? null;
    this.currentVersion = wire.currentVersion === undefined ? null : Number(wire.currentVersion);
  }

  get disposition(): ErrorDisposition {
    return dispositionFor(this.code);
  }
}

/**
 * A failure with no server answer: the socket closed, the request timed out, or
 * the browser is offline.
 *
 * This is always retryable. Critically, it is *ambiguous* for a mutation: the
 * server may have committed the operation before the connection died, which is
 * why the outbox resolves the operation through `GetOperationStatus` rather
 * than assuming it never happened.
 */
export class TransportError extends Error {
  readonly ambiguous: boolean;

  constructor(message: string, ambiguous: boolean) {
    super(message);
    this.name = 'TransportError';
    this.ambiguous = ambiguous;
  }
}

/**
 * Local persistence failed before anything was attempted on the network.
 *
 * Section 14: the interface must show "Could not save message on this device"
 * and must not show a queued indicator. Nothing was written, so there is no
 * message row to attach a status to — the error travels back to the caller
 * instead.
 */
export class LocalPersistenceError extends Error {
  readonly quotaExceeded: boolean;

  constructor(message: string, quotaExceeded: boolean, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'LocalPersistenceError';
    this.quotaExceeded = quotaExceeded;
  }
}

/**
 * Full jitter backoff.
 *
 * Section 05 fixes reconnect at exponential full jitter from one to sixty
 * seconds and Section 13 fixes mutation retry at `min(60s, 500ms x 2^attempt)`
 * with full jitter. Full jitter means a uniform draw across the whole window,
 * not the window plus a small wobble: it is the property that stops a thousand
 * clients that lost the same connection from returning in the same second.
 */
export function fullJitter(baseMs: number, attempt: number, capMs: number): number {
  const exponential = Math.min(capMs, baseMs * 2 ** Math.min(attempt, 30));
  return Math.random() * exponential;
}
